import {
  type AudioBackend,
  type AudioBus,
  type AudioClip,
  type AudioPlayback,
  type PlaybackRequest,
} from './AudioBackend';

const SILENT_CLIP: AudioClip = { durationSeconds: 0 };

const FINISHED_PLAYBACK: AudioPlayback = {
  isPlaying: false,
  stop: () => undefined,
};

/**
 * An audio backend that makes no sound.
 *
 * This is what runs where Web Audio does not exist: a test in Node, a server render, a browser with
 * audio disabled. Games call the same methods and get the same shapes back, so nothing above the
 * backend needs a "do we have audio" branch.
 *
 * A clip it did not decode is rejected, exactly as {@link WebAudioBackend} rejects one. Being the
 * lenient backend would hide a mixed-up clip everywhere except in the browser, which is the one place
 * it is expensive to find.
 */
export class SilentAudioBackend implements AudioBackend {
  #unlocked = false;

  /** Whether {@link SilentAudioBackend.unlock} has been called. */
  get isUnlocked(): boolean {
    return this.#unlocked;
  }

  /** Marks the backend unlocked. There is nothing to resume. */
  unlock(): Promise<void> {
    this.#unlocked = true;
    return Promise.resolve();
  }

  /** Returns a zero length clip without reading the bytes. */
  decode(_bytes: ArrayBuffer): Promise<AudioClip> {
    return Promise.resolve(SILENT_CLIP);
  }

  /** Discards the gain, there being no output to apply it to. */
  setBusGain(_bus: AudioBus, _gain: number): void {
    // Intentionally empty.
  }

  /** Returns a playback handle that has already finished. */
  play(clip: AudioClip, _request: PlaybackRequest): AudioPlayback {
    if (clip !== SILENT_CLIP) {
      throw new Error('This clip was not created by SilentAudioBackend.decode.');
    }
    return FINISHED_PLAYBACK;
  }

  /** Nothing is playing, so nothing stops. */
  stopAll(): void {
    // Intentionally empty.
  }

  /** Nothing is held, so nothing is released. */
  dispose(): void {
    // Intentionally empty.
  }
}
